import {
  CITIZENSHIP_TYPE,
  MINIMUM_ANNUAL_INCOME,
  MONTHS_PER_YEAR,
  NO_VERDICT,
  type EligibilityDetails,
  type EligibilityVerdict,
} from '../../types/eligibility';
import { parseMeasureNumber } from '../../utils/attributeMeasure';
import { attributeValuesOf } from './deriveFilterOptions';
import type { FilterableProduct } from './productFilterTypes';

/**
 * Whether the reader's details meet one lender's published minimum criteria
 * (CMS-1905 UC 2).
 *
 * ⚠️ This marks a loan. It never removes one and never reorders the list — the
 * one rule most likely to be "corrected" by a later reader, because these two
 * inputs sit inside a filter panel where every other control does filter. See
 * `docs/sort-and-filter.md` § 12.
 */
export const resolveEligibility = (
  product: FilterableProduct | undefined | null,
  details: EligibilityDetails,
): EligibilityVerdict => {
  const monthlyIncome = details.monthlyIncome;
  // Opt-in: with no income there is no outcome, on every loan (AC 5a). The
  // citizenship control alone never produces one.
  if (!product || !monthlyIncome || monthlyIncome <= 0) return NO_VERDICT;

  const income = product.attributes?.find(
    (a) => a?.name?.name === MINIMUM_ANNUAL_INCOME,
  );
  // `value_start` as well as `value`, the same fallback `attributeValuesOf` and
  // `findAttributeValue` use: a minimum authored as a range carries its figure
  // there, and reading only `value` would report the lender as publishing no
  // income criterion at all.
  const requiredAnnualIncome = parseMeasureNumber(
    income?.value ?? income?.value_start,
  );
  const accepted = attributeValuesOf(product, CITIZENSHIP_TYPE);

  // A lender that publishes neither is not judged (AC 3e). Distinct from
  // meeting the criteria, so it cannot claim an approval it has no basis for.
  const checkableCitizenship = !!details.citizenship && accepted.length > 0;
  if (requiredAnnualIncome == null && !checkableCitizenship) return NO_VERDICT;

  // Income first: when both fall short only the income reason shows (AC 3d).
  // It is the one the reader can act on, and the more specific of the two.
  if (
    requiredAnnualIncome != null &&
    monthlyIncome * MONTHS_PER_YEAR < requiredAnnualIncome
  ) {
    return { outcome: 'SHORT_INCOME', requiredAnnualIncome };
  }

  // Matched on the exact value, with no mapping between what the reader picked
  // and what the product carries (UC 1). A lender that accepts foreigners
  // generally and Work Permit holders specifically has to carry both values —
  // a population instruction, not a rule this can soften.
  if (checkableCitizenship && !accepted.includes(details.citizenship!)) {
    return { outcome: 'SHORT_CITIZENSHIP' };
  }

  return { outcome: 'MEETS' };
};

/**
 * Whether a loan is played down so the ones the reader meets stand out (UC 4).
 * Its Apply button is deliberately not — eligibility is guidance, never a gate.
 */
export const isPlayedDown = (
  verdict: EligibilityVerdict | undefined,
): boolean =>
  verdict?.outcome === 'SHORT_INCOME' ||
  verdict?.outcome === 'SHORT_CITIZENSHIP';
